import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { readStoredSession, resolveSession, wantsFreshLogin } from './session';
import { fetchRoadiesState, startRoadies, submitRoadies, linkRoadiesReport } from './api';
import { sendReport, extractReportIdFromResponse } from '../../functions/sendReport';

const errMsg = (e) => e?.response?.data?.message || e?.message || 'Something went wrong';

// The reports API takes "HH:MM:SS"; the game clock is "MM:SS" until it passes an hour.
export const reportTime = (timer) => (String(timer).split(':').length === 2 ? `00:${timer}` : String(timer));

// Log the player in (otp / demo / saved session) — see session.js.
export const loginRoadies = createAsyncThunk('roadies/login', async (_, { rejectWithValue }) => {
  try {
    return await resolveSession();
  } catch (e) {
    return rejectWithValue(errMsg(e));
  }
});

// Where is this player up to? (null until stage 1 has been opened.)
export const loadRoadiesState = createAsyncThunk('roadies/load', async (_, { getState, rejectWithValue }) => {
  try {
    return (await fetchRoadiesState(getState().roadies.session)).state;
  } catch (e) {
    return rejectWithValue(errMsg(e));
  }
});

// Push points/time to the ExtraMilePlay reports API (add on the first call, update after) and store the
// returned report id on the player's `stat` row (s1.php's successResponse()). Calls are queued so two
// of them can never both "add" a report for the same player.
let reportQueue = Promise.resolve();
export const syncReport = createAsyncThunk('roadies/syncReport', (snapshot, { getState, dispatch }) => {
  const run = async () => {
    const { session, reportId } = getState().roadies;
    if (!session || ['demobypass', 'DEMO'].includes(session.role)) return null;
    const response = await sendReport({
      sessionId: session.sessionId,
      organizationId: session.organizationId,
      userId: session.userId,
      role: session.role,
      token: session.token,
      gameId: session.gameId,
      name: session.name || '',
      points: snapshot.points,
      time: snapshot.time,
      reportId: reportId || undefined,
    });
    const id = extractReportIdFromResponse(response);
    if (id && id !== reportId) {
      dispatch(reportLinked(id));
      await linkRoadiesReport(session, id);
    }
    return id || reportId;
  };
  const p = reportQueue.then(run, run);
  reportQueue = p.catch(() => {});
  return p;
});

// Opening stage 1 creates the player's `stat` row (idempotent on the server) and their report (0 points).
export const startRoadiesGame = createAsyncThunk('roadies/start', async (_, { getState, dispatch, rejectWithValue }) => {
  try {
    const session = getState().roadies.session;
    const { state } = await startRoadies({
      ...session,
      name: session.name,
      email: session.email,
      role: session.role,
    });
    if (!state.reportId) dispatch(syncReport({ points: 0, time: '00:00:00' }));
    return state;
  } catch (e) {
    return rejectWithValue(errMsg(e));
  }
});

// submit.php: save one stage's cumulative score. Network hiccups are retried; a 4xx is a real answer.
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
export const submitStage = createAsyncThunk(
  'roadies/submit',
  async ({ round, score, timer }, { getState, dispatch, rejectWithValue }) => {
    const session = getState().roadies.session;
    let lastErr;
    for (let attempt = 0; attempt < 3; attempt++) {
      try {
        const res = await submitRoadies(session, round, score, timer);
        if (res.success) {
          dispatch(syncReport({ points: res.state.totalScore, time: reportTime(res.state.time || timer) }));
          return res.state;
        }
        if (res.returnData === 'ignored') return res.state; // already recorded (e.g. an earlier retry got through)
        return rejectWithValue(res.error || res.message || 'Score not accepted');
      } catch (e) {
        if (e?.response && e.response.status < 500) return rejectWithValue(errMsg(e));
        lastErr = e;
        await sleep(400 * (attempt + 1));
      }
    }
    return rejectWithValue(errMsg(lastErr));
  }
);

const slice = createSlice({
  name: 'roadies',
  initialState: {
    session: wantsFreshLogin() ? null : readStoredSession(),
    authStatus: 'idle', // idle -> loading -> succeeded | failed
    authError: null,
    starting: false,
    startTried: false, // start is attempted once per login, never in a loop
    game: null, // server state of the player's `stat` row, null until stage 1 is opened
    loaded: false,
    reportId: null,
    error: null,
  },
  reducers: {
    reportLinked: (s, a) => {
      s.reportId = a.payload;
    },
  },
  extraReducers: (b) => {
    b.addCase(loginRoadies.pending, (s) => {
      s.authStatus = 'loading';
    })
      .addCase(loginRoadies.fulfilled, (s, a) => {
        s.session = a.payload;
        s.authStatus = 'succeeded';
        s.authError = null;
        s.game = null; // progress belongs to whoever just logged in
        s.loaded = false;
        s.startTried = false;
        s.reportId = null;
      })
      .addCase(loginRoadies.rejected, (s, a) => {
        s.authStatus = 'failed';
        s.authError = a.payload;
      })
      .addCase(loadRoadiesState.fulfilled, (s, a) => {
        s.game = a.payload;
        if (a.payload?.reportId) s.reportId = a.payload.reportId;
        s.loaded = true;
        s.error = null;
      })
      .addCase(loadRoadiesState.rejected, (s, a) => {
        s.loaded = true;
        s.error = a.payload;
      })
      .addCase(startRoadiesGame.pending, (s) => {
        s.starting = true;
        s.startTried = true;
      })
      .addCase(startRoadiesGame.fulfilled, (s, a) => {
        s.starting = false;
        s.game = a.payload;
        if (a.payload?.reportId) s.reportId = a.payload.reportId;
        s.error = null;
      })
      .addCase(startRoadiesGame.rejected, (s, a) => {
        s.starting = false;
        s.error = a.payload;
      })
      .addCase(submitStage.fulfilled, (s, a) => {
        if (a.payload) s.game = a.payload;
      })
      .addCase(submitStage.rejected, (s, a) => {
        s.error = a.payload;
      })
      .addCase(syncReport.rejected, (s, a) => {
        console.error('roadies report failed:', a.error?.message);
      });
  },
});

export const { reportLinked } = slice.actions;
export default slice.reducer;
